import React from "react";
import "./explorecontainer.css";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import useAuth from "../../../hooks/useAuth";
import { ReactComponent as RepaymentIcon } from "../../../assets/navbar/repayment.svg";
import { ReactComponent as SaveIcon } from "../../../assets/navbar/save.svg";
import { ReactComponent as MembersIcon } from "../../../assets/navbar/members.svg";
import { ReactComponent as LoanIcon } from "../../../assets/navbar/loan.svg";
import { ReactComponent as StorageIcon } from "../../../assets/navbar/storage.svg";
import { ReactComponent as VouchersIcon } from "../../../assets/navbar/vouchers.svg";
import { ReactComponent as ApplyloansIcon } from "../../../assets/navbar/applyloans.svg";
import { ReactComponent as InterestIcon } from "../../../assets/navbar/interest.svg";
import { ReactComponent as PaymentsIcon } from "../../../assets/navbar/payments.svg";
import { ReactComponent as InvoiceIcon } from "../../../assets/navbar/invoice.svg";
import { ReactComponent as JobsIcon } from "../../../assets/navbar/jobs.svg";
import { ReactComponent as SettingsIcon } from "../../../assets/navbar/settings.svg";

// Line icons for the sections the console did not have before.
const stroke = { fill: "none", stroke: "currentColor", strokeWidth: 1.6, strokeLinecap: "round", strokeLinejoin: "round" };
const ReportsIcon = () => (
  <svg viewBox="0 0 24 24" style={{ color: "var(--text-primary)" }}>
    <rect x="4" y="3" width="16" height="18" rx="2" {...stroke} />
    <path d="M8 16v-3M12 16V9M16 16v-5" {...stroke} />
  </svg>
);
const BillingIcon = () => (
  <svg viewBox="0 0 24 24" style={{ color: "var(--text-primary)" }}>
    <circle cx="12" cy="9" r="5" {...stroke} />
    <path d="M9 13.5 7.5 21 12 18.5 16.5 21 15 13.5" {...stroke} />
  </svg>
);
const FixedDepositIcon = () => (
  <svg viewBox="0 0 24 24" style={{ color: "var(--text-primary)" }}>
    <rect x="5" y="10" width="14" height="10" rx="2" {...stroke} />
    <path d="M8 10V7a4 4 0 0 1 8 0v3M12 14v2" {...stroke} />
  </svg>
);
const RecurringDepositIcon = () => (
  <svg viewBox="0 0 24 24" style={{ color: "var(--text-primary)" }}>
    <rect x="4" y="5" width="16" height="15" rx="2" {...stroke} />
    <path d="M4 10h16M9 3v4M15 3v4M9.5 15.5l2 2 3.5-4" {...stroke} />
  </svg>
);
const RolesIcon = () => (
  <svg viewBox="0 0 24 24" style={{ color: "var(--text-primary)" }}>
    <path d="M12 3 5 6v5c0 4.4 3 8.3 7 10 4-1.7 7-5.6 7-10V6l-7-3Z" {...stroke} />
  </svg>
);

/**
 * Everything the console can show, grouped as the app groups it.
 *
 * "My money" and "The fund" are everybody's — the pages themselves show a
 * member their own records and someone whose role sees everyone's the whole
 * fund. "Admin" holds the fund's own tools, each shown only to a role that
 * holds the right for it, so nobody is offered a page the server would refuse.
 */
const groupsFor = (can) => [
  {
    title: "My money",
    items: [
      { label: "Savings", path: "save", Icon: SaveIcon },
      { label: "Loans", path: "loans", Icon: LoanIcon },
      { label: "Repayments", path: "repayment", Icon: RepaymentIcon },
      { label: "Invoices", path: "invoices", Icon: InvoiceIcon },
      { label: "Payments", path: "payments", Icon: PaymentsIcon },
      { label: "Apply for a loan", path: "applyloans", Icon: ApplyloansIcon },
    ],
  },
  {
    title: "The fund",
    items: [
      { label: "Members", path: "members", Icon: MembersIcon },
      { label: "Documents", path: "docs", Icon: StorageIcon },
      { label: "Interest records", path: "interestrecord", Icon: InterestIcon },
      { label: "Vouchers", path: "vouchers", Icon: VouchersIcon },
      can("fixed_deposits") && { label: "Fixed deposits", path: "fixed-deposits", Icon: FixedDepositIcon },
      can("recurring_deposits") && { label: "Recurring deposits", path: "recurring-deposits", Icon: RecurringDepositIcon },
    ].filter(Boolean),
  },
  {
    title: "Admin",
    items: [
      can("payments", "add") && { label: "Take payment", path: "take-payment", Icon: PaymentsIcon },
      can("settings") && { label: "Settings", path: "settings", Icon: SettingsIcon },
      can("jobs") && { label: "Jobs", path: "jobs", Icon: JobsIcon },
      can("reports") && { label: "Reports", path: "reports", Icon: ReportsIcon },
      can("billing") && { label: "Billing", path: "billing", Icon: BillingIcon },
      can("security") && { label: "Roles and permissions", path: "roles", Icon: RolesIcon },
    ].filter(Boolean),
  },
];

function ExploreContainer() {
  const navigate = useNavigate();
  const { can } = useAuth();

  return (
    <div>
      {groupsFor(can)
        .filter((group) => group.items.length)
        .map((group) => (
          <section key={group.title}>
            <h4 className="explore-group-title">{group.title}</h4>
            <main className="explore-container">
              {group.items.map(({ label, path, Icon }, index) => (
                <motion.div
                  key={path}
                  className="explore-icon explore-icon-loans"
                  onClick={() => navigate(`/admin/${path}`)}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.15, delay: 0.03 * index }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => event.key === "Enter" && navigate(`/admin/${path}`)}
                >
                  <Icon />
                  <label>{label}</label>
                </motion.div>
              ))}
            </main>
          </section>
        ))}
    </div>
  );
}

export default ExploreContainer;
